/**
 * Comments and the per-document chat (slate-feature-transfer W8 §C.4, §C.8 — S9 and S10).
 *
 * Two namespaces, one module, because they are the wave's only ENVELOPED families and the
 * envelope is the thing that bites:
 *
 *  - `comments.*` answers `{data, policies}` (and `{pagination, data, policies}` on list) —
 *    the only such family in W8. The policies block is parsed and carried, but nothing in
 *    slate reads it as permission: `BuildPolicies` sets all 34 ability keys `true` for every
 *    id, so it is decoration. The real gate is the host document's own policy plus
 *    own-authorship, which the caller applies (W8 §0.3, and the same finding as W5).
 *  - `documents.chat.*` answers `{data}` and is the one properly gated pair in the wave. Its
 *    403 has an EMPTY body, so the adapter supplies the sentence.
 *
 * `reactions.list` is **ungated server-side** (W8-4) — no `[Authorize]`, no manual check. It
 * is read here only to fold emoji counts the server's own cache column never fills (W8-5),
 * and must never be treated as an authenticated source.
 */

import {
  type Comment,
  type CommentDraft,
  type CommentListQuery,
  type CommentPage,
  CommentSchema,
  commentDoc,
  DOCUMENT_CHAT_MAX_CHARS,
  type DocumentChatMessage,
  DocumentChatMessageSchema,
  foldReactions,
  type ReactionRow,
  ReactionRowSchema,
  type ReactionSummary,
} from "~/api/commentTypes";
import { ApiError, ForbiddenError } from "~/api/errors";

/** What this module needs from the class that owns the bearer. */
export interface CommentTransport {
  /** Outline-era dot-RPC. Returns the WHOLE envelope so `pagination` survives. */
  rpc(
    path: string,
    body?: Record<string, unknown>,
  ): Promise<{
    data: unknown;
    pagination?: { offset: number; limit: number; total: number };
    policies?: Array<{ id: string; abilities: Record<string, boolean> }>;
  }>;
}

const obj = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
const arr = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const parseComment = (raw: unknown): Comment => CommentSchema.parse(obj(raw));

export function createKnowItComments(transport: CommentTransport) {
  const comments = {
    list: async (query: CommentListQuery): Promise<CommentPage> => {
      const envelope = await transport.rpc("comments.list", {
        ...(query.documentId ? { documentId: query.documentId } : {}),
        ...(query.parentCommentId ? { parentCommentId: query.parentCommentId } : {}),
        ...(query.collectionId ? { collectionId: query.collectionId } : {}),
        ...(query.sort ? { sort: query.sort } : {}),
        ...(query.direction ? { direction: query.direction } : {}),
        ...(query.statusFilter && query.statusFilter.length > 0
          ? { statusFilter: query.statusFilter }
          : {}),
        offset: query.offset ?? 0,
        limit: query.limit ?? 100,
      });
      const pagination = envelope.pagination;
      return {
        comments: arr(envelope.data).map(parseComment),
        total: pagination?.total ?? arr(envelope.data).length,
        offset: pagination?.offset ?? 0,
        limit: pagination?.limit ?? query.limit ?? 100,
      };
    },

    get: async (id: string): Promise<Comment> =>
      parseComment((await transport.rpc("comments.info", { id })).data),

    /**
     * ⚠ The id is NOT minted here for a thread root. S10's whole design is that a comment
     * thread's id **is** its conversation's id, so the caller mints one `uuid` and uses it in
     * both places. The server accepts a client-supplied id on the conversation side; here it
     * assigns one, so a think thread is created conversation-first and the comment root is
     * matched to it by that same id (see `features/comments/ThinkThread`).
     */
    create: async (draft: CommentDraft): Promise<Comment> =>
      parseComment(
        (
          await transport.rpc("comments.create", {
            documentId: draft.documentId,
            data: draft.data ?? commentDoc(draft.text ?? ""),
            ...(draft.parentCommentId ? { parentCommentId: draft.parentCommentId } : {}),
          })
        ).data,
      ),

    update: async (id: string, text: string): Promise<Comment> =>
      parseComment((await transport.rpc("comments.update", { id, data: commentDoc(text) })).data),

    /** Soft: the row is marked deleted. Answers `{success:true}`, never the comment. */
    remove: async (id: string): Promise<void> => {
      await transport.rpc("comments.delete", { id });
    },

    /** ⚠ Throws server-side for a REPLY — resolution is a property of the thread root. */
    resolve: async (id: string): Promise<Comment> =>
      parseComment((await transport.rpc("comments.resolve", { id })).data),

    unresolve: async (id: string): Promise<Comment> =>
      parseComment((await transport.rpc("comments.unresolve", { id })).data),

    addReaction: async (id: string, emoji: string): Promise<void> => {
      await transport.rpc("comments.add_reaction", { id, emoji });
    },

    removeReaction: async (id: string, emoji: string): Promise<void> => {
      await transport.rpc("comments.remove_reaction", { id, emoji });
    },

    /**
     * W8-5 — the presented `reactions` array is always empty because the aggregate's cache is
     * never refreshed. This reads the rows and folds them, so a count shown to a reader is one
     * we actually counted.
     */
    reactions: async (commentId: string, offset = 0, limit = 100): Promise<ReactionSummary[]> => {
      const envelope = await transport.rpc("reactions.list", { commentId, offset, limit });
      const rows: ReactionRow[] = arr(envelope.data).map((row) =>
        ReactionRowSchema.parse(obj(row)),
      );
      return foldReactions(rows);
    },
  };

  const documentChat = {
    list: async (documentId: string, limit = 100): Promise<DocumentChatMessage[]> => {
      const envelope = await transport.rpc("documents.chat.list", { documentId, limit });
      return arr(envelope.data).map((row) => DocumentChatMessageSchema.parse(obj(row)));
    },

    /**
     * The server trims and then TRUNCATES at 8,000 characters without saying so. Truncating
     * here as well is not belt-and-braces — it is what lets the composer show the limit
     * before somebody loses the end of a long message.
     *
     * A 403 arrives with an empty body (`Forbid()`), so the sentence is ours.
     */
    send: async (documentId: string, text: string): Promise<DocumentChatMessage> => {
      const trimmed = text.trim().slice(0, DOCUMENT_CHAT_MAX_CHARS);
      try {
        const envelope = await transport.rpc("documents.chat.send", {
          documentId,
          text: trimmed,
        });
        return DocumentChatMessageSchema.parse(obj(envelope.data));
      } catch (error) {
        if (
          error instanceof ForbiddenError ||
          (error instanceof ApiError && error.status === 403)
        ) {
          throw new ForbiddenError("You do not have access to this document's chat");
        }
        throw error;
      }
    },
  };

  return { comments, documentChat };
}
